import React from 'react';
import { AlertTriangle, AlertCircle, ArrowRight, CheckCircle } from 'lucide-react';
import { MATERIALITY_ISSUES } from './SectionB';
import { RISK_ISSUE_MAP } from './SectionD';

const KPI_FIELDS = [
  { field: 'data_breaches', label: '1.1 Data breaches' },
  { field: 'attrition_rate', label: '2.1 Attrition rate' },
  { field: 'training_hours', label: '2.2 Training hours' },
  { field: 'electricity_kwh', label: '3.1 Electricity consumption' },
  { field: 'renewable_energy_pct', label: '3.2 Renewable energy %' },
  { field: 'carbon_emissions', label: '3.3 Scope 1 & 2 emissions' },
  { field: 'ewaste_recycled_pct', label: '3.4 IT equipment recycled %' },
  { field: 'local_hiring_pct', label: '4.1 Local hiring %' },
  { field: 'community_investment', label: '4.2 Community investment' },
  { field: 'ai_automation_pct', label: '4.3 AI automation %' },
  { field: 'living_wage_pct', label: '4.4 Living wage %' },
];

const issueName = (id) => (MATERIALITY_ISSUES.find(i => i.id === id) || {}).name || id;

export function buildFindings(data) {
  const findings = [];
  const sa = data.section_a || {};
  const sb = data.section_b || {};
  const sc = data.section_c || {};
  const sd = data.section_d || {};

  if (!sa.total_employees) findings.push({ sectionIdx: 0, type: 'gap', message: 'A1 — Total employees is blank' });
  if (!(sa.business_segments || []).length) findings.push({ sectionIdx: 0, type: 'gap', message: 'A2 — No business segments selected' });
  if (!sa.operational_sites) findings.push({ sectionIdx: 0, type: 'gap', message: 'A3 — Operational sites is blank' });

  const unrated = MATERIALITY_ISSUES.filter(i => {
    const b = sb[i.id];
    return !b || !(b.financial > 0) || !(b.impact > 0);
  });
  if (unrated.length > 0) {
    const names = unrated.slice(0, 4).map(i => i.name).join(', ');
    findings.push({
      sectionIdx: 1, type: 'gap',
      message: `${unrated.length} of ${MATERIALITY_ISSUES.length} issues not fully rated (${names}${unrated.length > 4 ? ', …' : ''})`
    });
  }

  const blankKpis = KPI_FIELDS.filter(({ field }) => {
    if (field === 'carbon_emissions' && sc.carbon_emissions_na) return false;
    return sc[field] === undefined || sc[field] === null;
  });
  if (blankKpis.length > 0) {
    findings.push({
      sectionIdx: 2, type: 'gap',
      message: `KPI fields left blank: ${blankKpis.map(k => k.label).join(', ')}`
    });
  }

  if (sd.has_esg_targets === undefined || sd.has_esg_targets === null) {
    findings.push({ sectionIdx: 3, type: 'gap', message: 'D1 — ESG targets question not answered' });
  }
  if (sd.has_esg_targets === true && !sd.carbon_target_2030) {
    findings.push({ sectionIdx: 3, type: 'gap', message: 'D2 — Carbon reduction target is blank although you have ESG targets' });
  }
  const risks = sd.top_risks || [];
  if (risks.length < 3) {
    findings.push({ sectionIdx: 3, type: 'gap', message: `D3 — Only ${risks.length} of 3 top risks selected` });
  }
  risks.forEach(risk => {
    const issueId = RISK_ISSUE_MAP[risk];
    if (!issueId) return;
    const b = sb[issueId];
    const max = b ? Math.max(b.financial || 0, b.impact || 0) : 0;
    if (max > 0 && max <= 2) {
      findings.push({
        sectionIdx: 3, type: 'warning',
        message: `D3 — "${risk}" is a top risk, but you rated "${issueName(issueId)}" low (${max}/5) in Section B`
      });
    }
  });

  return findings;
}

const SECTION_LETTERS = ['A', 'B', 'C', 'D'];

export function ConsistencyCheckModal({ findings, onGoToSection, onSubmitAnyway, onClose, saving }) {
  const gaps = findings.filter(f => f.type === 'gap');
  const warnings = findings.filter(f => f.type === 'warning');
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="consistency-check-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto p-6">
        <div className="flex items-center gap-3 mb-1">
          <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0" />
          <h3 className="text-lg font-bold text-primary">Review before you submit</h3>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          We found {findings.length} item{findings.length > 1 ? 's' : ''} worth a second look. You can fix them now or submit anyway.
        </p>
        <div className="space-y-2 mb-5">
          {[...warnings, ...gaps].map((f, i) => (
            <div
              key={i}
              className={`flex items-start justify-between gap-3 p-3 rounded-xl border text-sm ${
                f.type === 'warning' ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-slate-200 bg-slate-50 text-slate-700'
              }`}
              data-testid={`finding-${i}`}
            >
              <div className="flex items-start gap-2">
                <AlertCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${f.type === 'warning' ? 'text-amber-500' : 'text-slate-400'}`} />
                <span>{f.message}</span>
              </div>
              <button
                onClick={() => onGoToSection(f.sectionIdx)}
                className="flex items-center gap-1 text-xs font-semibold text-accent-dark hover:underline whitespace-nowrap mt-0.5"
                data-testid={`goto-section-${SECTION_LETTERS[f.sectionIdx].toLowerCase()}-${i}`}
              >
                Section {SECTION_LETTERS[f.sectionIdx]}
                <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 btn-secondary h-11"
            data-testid="fix-responses-btn"
          >
            Fix responses
          </button>
          <button
            onClick={onSubmitAnyway}
            disabled={saving}
            className="flex-1 btn-accent h-11 flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="submit-anyway-btn"
          >
            <CheckCircle className="w-4 h-4" />
            Submit anyway
          </button>
        </div>
      </div>
    </div>
  );
}
